<script setup>
import OutbreakService from '@/services/quality/outbreak.service'
import LineListing from '@/views/quality/infection-control/outbreak/LineListing.vue'
import EpiCurveGraph from '@/views/quality/infection-control/outbreak/EpiCurveGraph.vue'
import ControlMeasures from '@/views/quality/infection-control/outbreak/ControlMeasures.vue'
import OutbreakHeader from '@/views/quality/infection-control/outbreak/OutbreakHeader.vue'
import OutbreakRcaDrawer from '@/views/quality/infection-control/outbreak/OutbreakRcaDrawer.vue'
import RiskPromoteDialog from '@/views/quality/risk/RiskPromoteDialog.vue'

const props = defineProps({
  id: {
    type: [String, Number],
    required: true,
  },
})

definePage({
  meta: {
    subject: 'quality.infection_control.outbreak',
    action: 'view',
    navActiveLink: 'app-quality-infection-control-outbreak',
  },
})

const activeTab = ref('line-listing')

const {
  data: outbreakData,
  execute: fetchOutbreak,
} = useApi(`/infection-control/outbreaks/${props.id}`)

const outbreak = computed(() => outbreakData.value ?? null)

const router = useRouter()

const isRcaDrawerOpen = ref(false)

const viewRca = () => {
  if (!outbreak.value?.rca) return

  const { href } = router.resolve({ name: 'app-quality-rca-view', params: { type: 'outbreak', id: outbreak.value.rca.id } })

  window.open(href, '_blank')
}

// Promote to Risk
const { isOpen: isPromoteDialogOpen, source: promoteSource, promptPromote } = useRiskPromotion()

const openPromoteDialog = () => {
  const parts = []

  if (outbreak.value?.organism?.name) parts.push(`Organism: ${outbreak.value.organism.name}`)
  if (outbreak.value?.location?.name) parts.push(`Location: ${outbreak.value.location.name}`)
  if (outbreak.value?.start_date) parts.push(`Started: ${outbreak.value.start_date}`)

  promptPromote('OUTBREAK', outbreak.value.id, outbreak.value.unit_id, {
    title: outbreak.value?.title ?? '',
    description: parts.join('. '),
  })
}

const onPromoted = risk => {
  router.push({ name: 'app-quality-risk-id', params: { id: risk.id } })
}

// Close outbreak dialog
const isCloseDialogVisible = ref(false)
const closeForm = ref({ notes: null })
const refCloseForm = ref()
const isClosing = ref(false)

const handleCloseOutbreak = async () => {
  const { valid } = await refCloseForm.value.validate()

  if (!valid) return

  isClosing.value = true
  await OutbreakService.close(
    props.id,
    closeForm.value,
    () => {
      isCloseDialogVisible.value = false
      closeForm.value = { notes: null }
      fetchOutbreak()
    },
    () => {},
  )
  isClosing.value = false
}

const showScrollTop = ref(false)

const onScroll = () => {
  showScrollTop.value = window.scrollY > 300
}

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

onMounted(() => window.addEventListener('scroll', onScroll, { passive: true }))
onBeforeUnmount(() => window.removeEventListener('scroll', onScroll))
</script>

<template>
  <section>
    <!-- Header card -->
    <OutbreakHeader
      :outbreak="outbreak"
      :cases="outbreak?.cases ?? []"
      @close="isCloseDialogVisible = true"
      @start-rca="isRcaDrawerOpen = true"
      @view-rca="viewRca"
      @promote="openPromoteDialog"
    />

    <OutbreakRcaDrawer
      v-if="outbreak"
      v-model:is-drawer-open="isRcaDrawerOpen"
      :outbreak="outbreak"
      @created="fetchOutbreak"
    />

    <!-- Tabs -->
    <VCard
      variant="flat"
      class="border"
    >
      <VTabs v-model="activeTab">
        <VTab value="line-listing">
          Line Listing
          <VChip
            v-if="(outbreak?.cases?.length ?? 0)"
            size="x-small"
            variant="tonal"
            color="secondary"
            class="ms-2"
          >
            {{ outbreak.cases.length }}
          </VChip>
        </VTab>
        <VTab value="epi-curve">
          EPI Curve
        </VTab>
        <VTab value="control-measures">
          Control Measures
          <VChip
            v-if="(outbreak?.measures?.length ?? 0)"
            size="x-small"
            variant="tonal"
            color="secondary"
            class="ms-2"
          >
            {{ outbreak.measures.length }}
          </VChip>
        </VTab>
      </VTabs>

      <!-- <VDivider /> -->

      <VWindow v-model="activeTab">
        <VWindowItem value="line-listing">
          <LineListing
            :cases="outbreak?.cases ?? []"
            :outbreak-id="Number(props.id)"
            @case-added="fetchOutbreak"
          />
        </VWindowItem>

        <VWindowItem value="epi-curve">
          <EpiCurveGraph :cases="outbreak?.cases ?? []" />
        </VWindowItem>

        <VWindowItem value="control-measures">
          <ControlMeasures
            :measures="outbreak?.measures ?? []"
            :outbreak-id="Number(props.id)"
            @measure-added="fetchOutbreak"
          />
        </VWindowItem>
      </VWindow>
    </VCard>

    <!-- Close Outbreak Dialog -->
    <VDialog
      v-model="isCloseDialogVisible"
      max-width="500"
      persistent
      no-click-animation
    >
      <VCard>
        <VCardItem class="py-3 px-4">
          <template #title>
            <div class="d-flex align-center gap-2">
              <IconBtn
                variant="tonal"
                color="error"
                size="small"
              >
                <VIcon
                  icon="tabler-lock"
                  size="22"
                  color="error"
                />
              </IconBtn>
              <span class="text-h6 font-weight-medium">Close Outbreak</span>
            </div>
          </template>
          <template #append>
            <IconBtn @click="isCloseDialogVisible = false">
              <VIcon icon="tabler-x" />
            </IconBtn>
          </template>
        </VCardItem>

        <VDivider />

        <VCardText class="pa-4">
          <p class="text-body-2 text-medium-emphasis mb-4">
            Please provide closing notes before marking this outbreak as closed.
          </p>
          <VForm ref="refCloseForm">
            <AppTextarea
              v-model="closeForm.notes"
              label="Closing Notes"
              placeholder="Enter closing notes"
              :rules="[requiredValidator]"
              :required="true"
              :is-speech-to-text="false"
            />
          </VForm>
        </VCardText>

        <VDivider />

        <VCardActions class="pa-4 gap-2">
          <VSpacer />
          <VBtn
            variant="tonal"
            color="secondary"
            @click="isCloseDialogVisible = false"
          >
            Cancel
          </VBtn>
          <VBtn
            color="error"
            variant="flat"
            :loading="isClosing"
            @click="handleCloseOutbreak"
          >
            Close Outbreak
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VFadeTransition>
      <VBtn
        v-show="showScrollTop"
        icon="tabler-arrow-up"
        color="primary"
        size="small"
        class="outbreak-scroll-top"
        aria-label="Scroll to top"
        @click="scrollToTop"
      />
    </VFadeTransition>

    <!-- Promote to Risk -->
    <RiskPromoteDialog
      v-model:is-dialog-open="isPromoteDialogOpen"
      :source-type="promoteSource.type"
      :source-id="promoteSource.id"
      :source-unit-id="promoteSource.unitId"
      :prefill="promoteSource.prefill"
      @promoted="onPromoted"
    />
  </section>
</template>

<style scoped lang="scss">
.outbreak-scroll-top {
  position: fixed;
  inset-block-end: 24px;
  inset-inline-end: 24px;
  z-index: 10;
}
</style>
